/**
 * Pure G703 grid helpers — billing % vs retention % must stay independent.
 */

import type { PayAppLine } from '../api/types'
import { isCoLine } from './coLabel'

export interface G703DraftSlice {
  id: number
  _thisPct: string
  _retPct: string
  _retTouched?: boolean
}

export interface G703EditableLine extends PayAppLine {
  _thisPct: string
  _retPct: string
  _serverRetPct: string
  _retTouched: boolean
}

export function normalizePctString(raw: string | number | null | undefined): string {
  if (raw == null || raw === '') return '0'
  return String(raw)
}

export function toEditableLine(line: PayAppLine, draft?: G703DraftSlice): G703EditableLine {
  const serverRet = normalizePctString(line.retainage_pct)
  const base: G703EditableLine = {
    ...line,
    _thisPct: normalizePctString(line.this_pct),
    _retPct: serverRet,
    _serverRetPct: serverRet,
    _retTouched: false,
  }
  if (!draft) return base
  return {
    ...base,
    _thisPct: draft._thisPct ?? base._thisPct,
    _retPct: draft._retTouched ? (draft._retPct ?? serverRet) : serverRet,
    _retTouched: draft._retTouched === true,
  }
}

/** True when prior + this period would exceed 100% billed. */
export function lineExceeds100(
  line: Pick<G703EditableLine, 'prev_pct'> & { this_pct?: string | number; _thisPct?: string },
): boolean {
  const prev = parseFloat(String(line.prev_pct ?? '')) || 0
  const th = parseFloat(String(line._thisPct ?? line.this_pct ?? '')) || 0
  return prev + th > 100 + 1e-9
}

export function anyLineExceeds100(
  lines: Array<Pick<G703EditableLine, 'prev_pct'> & { this_pct?: string | number; _thisPct?: string }>,
): boolean {
  return (lines ?? []).some(lineExceeds100)
}

export function overbillLineMessage(
  line: Pick<G703EditableLine, 'prev_pct' | 'item_id'> & { this_pct?: string | number; _thisPct?: string },
): string {
  const prev = parseFloat(String(line.prev_pct ?? '')) || 0
  const th = parseFloat(String(line._thisPct ?? line.this_pct ?? '')) || 0
  const item = String(line.item_id ?? '').trim()
  if (!item || item.toLowerCase() === 'line') {
    return `This line would exceed 100% billed (previous ${prev}% + this period ${th}%). Adjust this period.`
  }
  return `Line ${item} would exceed 100% billed (previous ${prev}% + this period ${th}%). Adjust this period.`
}

/** Bulk apply / auto-fill — billing column only; never mutate retention. Caps at remainder. */
export function applyBillingPctToAll(lines: G703EditableLine[], bulkPct: string): G703EditableLine[] {
  return lines.map(l => {
    const requested = parseFloat(bulkPct)
    if (!Number.isFinite(requested)) return { ...l, _thisPct: bulkPct }
    const max = parseFloat(balanceToBillPctForLine(l)) || 0
    const applied = Math.min(Math.max(0, requested), max)
    return { ...l, _thisPct: String(applied) }
  })
}

/** Set every line's RET % to project default; billing (THIS $) unchanged. */
export function applyDefaultRetainageToAll(
  lines: G703EditableLine[],
  defaultPct: string | number,
): G703EditableLine[] {
  const pct = normalizePctString(defaultPct)
  return lines.map(l => ({ ...l, _retPct: pct, _retTouched: true }))
}

/** Apply project default only to lines below that default. */
export function applyDefaultRetainageToBelow(
  lines: G703EditableLine[],
  defaultPct: string | number,
): G703EditableLine[] {
  const floor = parseFloat(String(defaultPct)) || 0
  const pct = normalizePctString(defaultPct)
  return lines.map((l) => {
    if (isCoLine(l.item_id)) return l
    const ret = parseFloat(String(l._retPct)) || 0
    if (ret < floor - 1e-9) return { ...l, _retPct: pct, _retTouched: true }
    return l
  })
}

export function allBillingPctZero(lines: Pick<G703EditableLine, '_thisPct'>[]): boolean {
  return lines.length > 0 && lines.every(l => {
    const n = parseFloat(l._thisPct)
    return n === 0 || Number.isNaN(n)
  })
}

/** CO lines: trust server billing/retainage when set; else bill remainder at 0% retainage. */
export function applyCoLineBillingDefaults(lines: G703EditableLine[]): G703EditableLine[] {
  return lines.map(l => {
    if (!isCoLine(l.item_id)) return l
    if (l._retTouched) return l
    const serverThis = parseFloat(String(l.this_pct ?? '')) || 0
    const serverRet = normalizePctString(l.retainage_pct)
    const thisPct = serverThis > 0 ? normalizePctString(l.this_pct) : balanceToBillPctForLine(l)
    return {
      ...l,
      _thisPct: thisPct,
      _retPct: serverRet,
      _serverRetPct: serverRet,
      _retTouched: false,
    }
  })
}

/** Apply displayed default billing % when every line is still at zero (save-safe). */
export function ensureBillingDefaults(
  lines: G703EditableLine[],
  defaultPct: string = '20',
): G703EditableLine[] {
  if (!allBillingPctZero(lines)) return applyCoLineBillingDefaults(lines)
  const coLines = applyCoLineBillingDefaults(lines.filter(l => isCoLine(l.item_id)))
  const baseLines = applyBillingPctToAll(lines.filter(l => !isCoLine(l.item_id)), defaultPct)
  const byId = new Map([...baseLines, ...coLines].map(l => [l.id, l]))
  return lines.map(l => byId.get(l.id) ?? l)
}

export function updateEditableLine(
  lines: G703EditableLine[],
  idx: number,
  key: '_thisPct' | '_retPct',
  value: string,
): G703EditableLine[] {
  return lines.map((l, i) => {
    if (i !== idx) return l
    if (key === '_retPct') {
      return { ...l, _retPct: value, _retTouched: true }
    }
    return { ...l, _thisPct: value }
  })
}

export function buildLinesSavePayload(lines: G703EditableLine[]) {
  return lines.map(l => ({
    id: l.id,
    this_pct: parseFloat(l._thisPct) || 0,
    retainage_pct: l._retTouched
      ? (parseFloat(l._retPct) || 0)
      : (parseFloat(l._serverRetPct) || 0),
    stored_materials: parseFloat(l.stored_materials ?? '0') || 0,
  }))
}

export function toDraftSlice(line: G703EditableLine): G703DraftSlice {
  return {
    id: line.id,
    _thisPct: line._thisPct,
    _retPct: line._retPct,
    _retTouched: line._retTouched,
  }
}

/** Highest line retention % on saved payload (for step-5 soft warning). */
export function maxRetainagePctFromPayload(
  payload: Array<{ retainage_pct: number }>,
): number {
  return payload.reduce((max, l) => Math.max(max, l.retainage_pct || 0), 0)
}

export const RETAINAGE_WARN_THRESHOLD = 15

/** Remaining % to bill on one line: prior + this = 100%. Does not touch G702 math. */
export function balanceToBillPctForLine(line: Pick<G703EditableLine, 'prev_pct'>): string {
  const prev = parseFloat(line.prev_pct) || 0
  if (prev >= 100) return '0'
  const remainder = Math.round((100 - prev) * 100) / 100
  return remainder <= 0 ? '0' : String(remainder)
}

/** Set each line's _thisPct to bill exactly its remaining balance. */
export function applyBalanceToBillAll(lines: G703EditableLine[]): G703EditableLine[] {
  return lines.map(l => ({ ...l, _thisPct: balanceToBillPctForLine(l) }))
}

/** Withheld retainage $ this period = Σ (sched × this% × ret%). */
export function computeWithheldThisPeriod(
  lines: Array<Pick<G703EditableLine, 'scheduled_value' | '_thisPct' | '_retPct' | '_retTouched' | '_serverRetPct' | 'this_pct' | 'retainage_pct'>>,
): number {
  return lines.reduce((sum, l) => {
    const sv = parseFloat(l.scheduled_value) || 0
    const thisPct = parseFloat(l._thisPct ?? l.this_pct ?? '0') || 0
    const retPct = l._retPct != null
      ? (l._retTouched
        ? (parseFloat(l._retPct) || 0)
        : (parseFloat(l._serverRetPct ?? l._retPct ?? l.retainage_pct ?? '0') || 0))
      : (parseFloat(l.retainage_pct ?? '0') || 0)
    return sum + sv * thisPct / 100 * retPct / 100
  }, 0)
}

/** True when every line with sched value > 0 is at 100% cumulative after this period. */
export function allLinesFullyBilled(lines: G703EditableLine[]): boolean {
  return lines.every(l => {
    const sv = parseFloat(l.scheduled_value) || 0
    if (sv <= 0) return true
    const prev = parseFloat(l.prev_pct) || 0
    const thisPct = parseFloat(l._thisPct) || 0
    return prev + thisPct >= 99.99
  })
}
